/**
 * Module chrome shared by the dockview group header and, in Tradovate style, the chart module's own
 * bar (owner 2026-09-29: "one header row like Tradovate").
 *
 * - `ModuleMenu`: one "⋯" with the module's options and Maximize / Float / Pop out in words (the
 *   group header's right-hand action, `Workspace` → `GroupActions`; owner 2026-10-04).
 * - **Headerless chart groups**: in Tradovate style a group holding only chart modules (docked in
 *   the grid or in a pop-out window) hides dockview's tab row (`syncChartHeaders`); the chart's tab
 *   strip then carries the actions (`useChartModuleChrome`: the same buttons plus close) and a drag
 *   grip that starts dockview's own panel drag (the `PanelTransfer` a tab drag sets), so the
 *   module still docks, tabs, splits and floats like any other. Floating groups keep their header
 *   (it is how a floating window is moved), as does any group that also holds another module.
 */
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  LocalSelectionTransfer,
  PanelTransfer,
  type DockviewApi,
  type DockviewGroupPanel,
  type DockviewPanelApi,
  type IDockviewPanel,
} from "dockview-react";
import {
  LINK_GROUPS,
  chartGroupHeaderlessRule,
  effectiveVisualStyle,
  useLinkGroups,
  useSettings,
  type LinkTarget,
} from "@helm/core";
import { Icon } from "../components/Icon";
import { pushToast } from "../components/Toasts";
import { AutoCenterOption } from "../widgets/DomLadder";
import { DomControlsOptions } from "../widgets/OrderPad";
import { DEFAULT_PAD_MODE, type WidgetParams } from "./templates";

export type Chrome = { locked: boolean; close: (api: DockviewApi, panel: IDockviewPanel) => void };
export const ChromeContext = createContext<Chrome>({ locked: false, close: () => {} });

export function useGroupState(
  group: DockviewGroupPanel,
  api: DockviewApi,
): { location: string; maximized: boolean } {
  const read = (): { location: string; maximized: boolean } => ({
    location: group.api.location.type,
    maximized: group.api.location.type === "grid" && group.api.isMaximized(),
  });
  const [state, setState] = useState(read);
  useEffect(() => {
    const update = (): void => {
      setState(read());
    };
    update();
    const a = group.api.onDidLocationChange(update);
    const b = api.onDidMaximizedGroupChange(update);
    return () => {
      a.dispose();
      b.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `read` only closes over group
  }, [group, api]);
  return state;
}

/** The order-pad mode a module shows (DOM mode is where the ladder options apply). */
function padModeOf(params: WidgetParams): string | undefined {
  return params.mode ?? DEFAULT_PAD_MODE[params.kind];
}

/**
 * The module's own options at the top of its "⋯" menu: its link colour, and for a DOM ladder (or
 * an order pad in DOM mode) auto-centre and which pad controls show. Presentation only: nothing
 * here places, changes or cancels an order.
 */
function ModuleOptions({ panel }: { panel: IDockviewPanel }): React.JSX.Element | null {
  const [params, setParams] = useState(() => panel.params as WidgetParams | undefined);
  if (!params) return null;
  const update = (patch: Partial<WidgetParams>): void => {
    const next = { ...params, ...patch };
    panel.api.updateParameters(next);
    setParams(next);
  };
  const ladder = params.kind === "dom" || (PAD_KINDS.has(params.kind) && padModeOf(params) === "dom");
  const padDom = PAD_KINDS.has(params.kind) && padModeOf(params) === "dom";
  const linkable = !NO_LINK_KINDS.has(params.kind);
  if (!linkable && !ladder) return null;
  return (
    <div className="module-menu-options" role="group" aria-label={`${panel.title ?? "Module"} options`}>
      {linkable && (
        <label className="module-menu-link">
          Link
          <select
            className="select select-sm"
            value={params.link}
            aria-label="Link group"
            onChange={(e) => {
              const l = e.target.value as LinkTarget;
              // Unlinking keeps the symbol the module shows (as the link dot does).
              const shown =
                params.link === "unlinked" ? null : useLinkGroups.getState().groups[params.link].symbol;
              update(l === "unlinked" && shown ? { link: l, symbol: shown } : { link: l });
            }}
          >
            <option value="unlinked">○ unlinked</option>
            {LINK_GROUPS.map((g) => (
              <option key={g} value={g}>
                ● {g}
              </option>
            ))}
          </select>
        </label>
      )}
      {ladder && <AutoCenterOption />}
      {padDom && (
        <DomControlsOptions
          controls={params.domControls ?? {}}
          joinCollapsed={params.domJoinCollapsed ?? false}
          onControl={(key, value) => {
            update({ domControls: { ...(params.domControls ?? {}), [key]: value } });
          }}
          onJoinCollapsed={(collapsed) => {
            update({ domJoinCollapsed: collapsed });
          }}
        />
      )}
    </div>
  );
}

/** Order pads (their DOM mode carries the ladder options). */
const PAD_KINDS: ReadonlySet<string> = new Set(["execution", "scalp-pad", "ticket"]);
/** Account-level modules: no symbol, so no link colour. */
const NO_LINK_KINDS: ReadonlySet<string> = new Set([
  "positions",
  "orders",
  "fills",
  "accounts",
  "notifications",
  "risk",
  "journal",
  "copy",
]);

/**
 * One "⋯" per module header (owner 2026-10-04, replacing the maximize / float / pop-out icons):
 * the module's own options, then Maximize / Restore · Float · Pop out (or Return to the main
 * window / Dock into the layout), in words. Rendered into the group's own document (pop-out windows
 * included) so the header never clips it.
 */
export function ModuleMenu({
  group,
  containerApi,
  panel,
}: {
  group: DockviewGroupPanel;
  containerApi: DockviewApi;
  panel: IDockviewPanel | undefined;
}): React.JSX.Element {
  const { locked } = useContext(ChromeContext);
  const { location, maximized } = useGroupState(group, containerApi);
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState<{ top: number; right: number } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const close = (refocus = true): void => {
    setOpen(false);
    if (refocus) btn.current?.focus();
  };
  useEffect(() => {
    if (!open) return;
    const doc = btn.current?.ownerDocument ?? document;
    const onDown = (e: PointerEvent): void => {
      const t = e.target as Node;
      if (menu.current?.contains(t) || btn.current?.contains(t)) return;
      setOpen(false);
    };
    doc.addEventListener("pointerdown", onDown);
    menu.current?.querySelector<HTMLElement>("button, select, input")?.focus();
    return () => {
      doc.removeEventListener("pointerdown", onDown);
    };
  }, [open]);
  const act = (run: () => void) => (): void => {
    close(false);
    run();
  };
  const items: { label: string; title: string; run: () => void }[] = [];
  if (location === "grid")
    items.push(
      maximized
        ? {
            label: "Restore",
            title: "Back to the layout",
            run: () => {
              containerApi.exitMaximizedGroup();
            },
          }
        : {
            label: "Maximize",
            title: "Full size",
            run: () => {
              group.api.maximize();
            },
          },
    );
  if (!locked && location === "grid")
    items.push({
      label: "Float",
      title: "Float over the workspace",
      run: () => {
        containerApi.addFloatingGroup(group);
      },
    });
  if (location === "popout")
    items.push({
      label: "Return to main window",
      title: "Back where it was in the main window",
      // Closing the window puts the group back at its old place in the layout.
      run: () => {
        group.api.getWindow().close();
      },
    });
  else {
    if (location === "floating" && !locked)
      items.push({
        label: "Dock into layout",
        title: "Dock into the layout",
        run: () => {
          group.api.moveTo({ position: "right" });
        },
      });
    items.push({
      label: "Pop out",
      title: "Open in a separate window",
      run: () => {
        void containerApi.addPopoutGroup(group).then((ok) => {
          if (!ok)
            pushToast({
              tone: "warn",
              title: "Pop-out blocked",
              body: "Allow pop-ups for TradePrizm in the browser, then try again.",
            });
        });
      },
    });
  }
  const doc = btn.current?.ownerDocument ?? document;
  return (
    <>
      <button
        ref={btn}
        type="button"
        className={
          open ? "icon-btn icon-btn-xs module-menu-btn active" : "icon-btn icon-btn-xs module-menu-btn"
        }
        aria-label="Module menu"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Module options: maximize, float, pop out…"
        onClick={() => {
          const r = btn.current?.getBoundingClientRect();
          const w = btn.current?.ownerDocument.defaultView?.innerWidth ?? window.innerWidth;
          if (r) setAt({ top: r.bottom + 4, right: Math.max(4, w - r.right) });
          setOpen((o) => !o);
        }}
      >
        <Icon name="more" size={14} />
      </button>
      {open &&
        at &&
        createPortal(
          <div
            ref={menu}
            className="module-menu"
            role="menu"
            aria-label="Module menu"
            style={{ top: at.top, right: at.right }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.stopPropagation();
                close();
              }
            }}
          >
            {panel && <ModuleOptions key={panel.id} panel={panel} />}
            {items.map((it) => (
              <button
                key={it.label}
                type="button"
                role="menuitem"
                className="module-menu-item"
                title={it.title}
                onClick={act(it.run)}
              >
                {it.label}
              </button>
            ))}
          </div>,
          doc.body,
        )}
    </>
  );
}

// ------------------------------------------------------------------------------------------------
// Headerless chart groups (Tradovate style)

/** A group whose tab row the chart's own bar replaces (see the module comment; rule in core). */
export function chartGroupHeaderless(group: DockviewGroupPanel, tradovate: boolean): boolean {
  return chartGroupHeaderlessRule(
    group.panels.map((p) => (p.params as WidgetParams | undefined)?.kind),
    group.api.location.type,
    tradovate,
  );
}

/** Hide / show the tab row of every group per `chartGroupHeaderless` (only where it changes). */
export function syncChartHeaders(api: DockviewApi, tradovate: boolean): void {
  for (const g of api.groups) {
    const hide = chartGroupHeaderless(g, tradovate);
    if (g.header.hidden !== hide) g.header.hidden = hide;
  }
}

/** Current Tradovate-style flag (the setting everyone reads). */
export function useTradovateStyle(): boolean {
  return useSettings((s) => effectiveVisualStyle(s.settings) === "tradovate");
}

/** Whether this panel's group is headerless now (re-evaluated on every layout change). */
export function useHeaderless(panelApi: DockviewPanelApi, containerApi: DockviewApi): boolean {
  const tradovate = useTradovateStyle();
  const read = (): boolean => chartGroupHeaderless(panelApi.group, tradovate);
  const [on, setOn] = useState(read);
  useEffect(() => {
    const update = (): void => {
      setOn(chartGroupHeaderless(panelApi.group, tradovate));
    };
    update();
    const a = containerApi.onDidLayoutChange(update);
    const b = panelApi.onDidGroupChange(update);
    const c = panelApi.onDidLocationChange(update);
    return () => {
      a.dispose();
      b.dispose();
      c.dispose();
    };
  }, [panelApi, containerApi, tradovate]);
  return on;
}

/**
 * The chart bar's module chrome while its group is headerless: a drag grip (fills the bar's free
 * space; dragging it is dragging the module's tab — dock, split, tab into another group, float)
 * and, at the right end, the group actions plus close.
 */
export function useChartModuleChrome({
  panelApi,
  containerApi,
}: {
  panelApi: DockviewPanelApi;
  containerApi: DockviewApi;
}): { grip: React.ReactNode; actions: React.ReactNode } {
  const { locked, close } = useContext(ChromeContext);
  const transfer = LocalSelectionTransfer.getInstance<PanelTransfer>();
  const grip = (
    <div
      className={locked ? "chart-drag-grip locked" : "chart-drag-grip"}
      draggable={!locked}
      aria-hidden="true"
      title={locked ? "Layout locked" : "Drag to move this chart module (dock, split, tab or float)"}
      onDragStart={(e) => {
        const group = panelApi.group;
        transfer.setData(
          [new PanelTransfer(containerApi.id, group.id, panelApi.id)],
          PanelTransfer.prototype,
        );
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", "");
      }}
      onDragEnd={() => {
        // dockview reads the transfer on drop; clear it after the drop handlers ran.
        setTimeout(() => {
          transfer.clearData(PanelTransfer.prototype);
        }, 0);
      }}
    />
  );
  const actions = (
    <div className="group-actions chart-module-actions">
      <ModuleMenu
        group={panelApi.group}
        containerApi={containerApi}
        panel={containerApi.getPanel(panelApi.id)}
      />
      {!locked && (
        <button
          type="button"
          className="icon-btn icon-btn-xs"
          aria-label="Close module"
          title="Close this chart module (Undo in the toast)"
          onClick={() => {
            const panel = containerApi.getPanel(panelApi.id);
            if (panel) close(containerApi, panel);
          }}
        >
          <Icon name="close" size={12} />
        </button>
      )}
    </div>
  );
  return { grip, actions };
}
